import React, { useState, useEffect, useRef } from 'react';
import NewGameView from './NewGame.view';
import useNewGame from './hook/useNewGame';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { validateStepWithToast } from './utils/validation';

interface Props {
  onClose: () => void;
  gameId?: string;
  onSaveComplete?: () => void;
}

const NewGameContainer: React.FC<Props> = ({
  onClose,
  gameId,
  onSaveComplete,
}) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isEditMode, setIsEditMode] = useState(false);
  const hasLoadedData = useRef(false);
  const isSavingRef = useRef(false);

  // Extract gameId from URL if not passed as prop
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gameIdParam = params.get('gameId');

    setIsEditMode(!!gameId || !!gameIdParam);
  }, [location, gameId]);

  const urlGameId = new URLSearchParams(location.search).get('gameId');
  const effectiveGameId = gameId || urlGameId || '';

  const {
    error,
    newGameState,
    loading,
    onChangeNewGameState,
    onSaveGame,
    shouldDisableNextOrFinishButton,
    errorMessages,
    setShouldDisableNextOrFinishButton,
    loadGameForEditing,
    resetNewGameState,
  } = useNewGame();

  const steps = ['Step 1', 'Step 2', 'Step 3'];
  const [activeStep, setActiveStep] = useState(0);

  // Load game data if in edit mode
  useEffect(() => {
    if (effectiveGameId && !loading) {
      loadGameForEditing(effectiveGameId);
    } else {
      resetNewGameState();
    }
  }, [effectiveGameId]);

  const onChangeActiveStep = async (
    newActiveStep: number
  ): Promise<boolean> => {
    if (loading || shouldDisableNextOrFinishButton) return false;
    if (newActiveStep < 0 || newActiveStep >= steps.length) return false;

    // If trying to go forward, validate the current step
    if (newActiveStep > activeStep) {
      const validationResult = await validateStepWithToast(
        newGameState,
        activeStep
      );
      if (!validationResult.isValid) {
        // Validation failed, don't proceed

        return false;
      }
    }

    // If validation passes or we're going backward, change the step
    setActiveStep(newActiveStep);

    return true;
  };

  const handleSaveGame = async () => {
    // Prevent duplicate submissions
    if (isSavingRef.current) return;
    isSavingRef.current = true;

    try {
      // Clean round objects before saving - remove problematic fields
      const cleanedState = { ...newGameState };
      if (cleanedState.bracket?.roundValues) {
        cleanedState.bracket.roundValues = cleanedState.bracket.roundValues.map(
          (round) => {
            const cleanRound: any = { ...round };
            // Keep currentRoundValues as empty array - backend expects it to exist
            cleanRound.currentRoundValues = [];
            // Remove other problematic nested fields
            delete cleanRound.roundValues;
            delete cleanRound.currentTotalCompetitors;
            delete cleanRound.currentRounds;
            delete cleanRound.bracket;
            return cleanRound; // Cleaned round object with proper structure for backend
          }
        );
      }

      // Sync main state fields from rounds for bracket games
      if (
        cleanedState.contestType?.includes('bracket') &&
        cleanedState.bracket?.roundValues
      ) {
        const rounds = cleanedState.bracket.roundValues;
        const firstRound = rounds[0];
        const lastRound = cleanedState.totalRounds
          ? rounds[cleanedState.totalRounds - 1]
          : null;

        if (!cleanedState.bracketName && firstRound?.bracketName) {
          cleanedState.bracketName = firstRound.bracketName;
        }
        if (!cleanedState.KPIRuleID && lastRound?.KPIRuleID) {
          cleanedState.KPIRuleID = lastRound.KPIRuleID;
        }
        if (!cleanedState.startDate && firstRound?.startDate) {
          cleanedState.startDate = firstRound.startDate;
        }
        if (!cleanedState.endDate && lastRound?.endDate) {
          cleanedState.endDate = lastRound.endDate;
        }
      }

      // Temporarily update state for saving
      onChangeNewGameState(cleanedState);

      // Call the save game function
      const result = await onSaveGame();

      // If result is null/undefined or not an object, treat as a successful create
      // and skip the cache update logic (the hook already handles cache invalidation)
      if (!result || typeof result !== 'object') {
        // Save succeeded (hook showed success toast) — just close the modal
        onClose();
        return result;
      }

      const now = Date.now(); // Current timestamp for comparison

      // Determine if this is an edit (has an ID) or create (no ID)
      const isEdit = Boolean(result?.id);

      if (isEdit) {
        // EDIT MODE - Directly update the cache without invalidation

        // 1. Update the details view cache if needed
        if (effectiveGameId) {
          queryClient.setQueryData(['game', effectiveGameId], result);
        }

        // 2. Determine which game type this edited game belongs to
        const gameStartDateEpoch =
          result.startDateEpoch * 1000 || new Date(result.startDate).getTime();
        const gameEndDateEpoch =
          result.endDateEpoch * 1000 || new Date(result.endDate).getTime();

        let gameType: number; // Add explicit type annotation
        if (gameEndDateEpoch < now) {
          gameType = 2; // Past Game (Type 2 in the enum)
        } else if (gameStartDateEpoch > now) {
          gameType = 1; // Future Game (Type 1 in the enum)
        } else {
          gameType = 0; // Current Game (Type 0 in the enum)
        }

        // 3. Get each relevant scope to check
        const scopes = ['all', 'person', 'circle']; // All possible scopes

        // 4. Update the game in each appropriate cache
        scopes.forEach((scope) => {
          // Get the exact game list from cache for this type and scope
          const queryKey = ['games', { type: gameType, scope }];
          const cachedData = queryClient.getQueryData(queryKey);

          if (Array.isArray(cachedData)) {
            // Find the exact index of the game to update
            const gameIndex = cachedData.findIndex(
              (game) => game.id === result.id
            );

            if (gameIndex !== -1) {
              // Create a new array with the updated game
              const updatedData = [...cachedData];

              // Update the specific fields from the result while preserving structure
              updatedData[gameIndex] = {
                ...cachedData[gameIndex], // Keep original structure
                name: result.name, // Update fields that likely changed
                description: result.description,
                startDate: result.startDate,
                endDate: result.endDate,
                startDateEpoch: result.startDateEpoch,
                endDateEpoch: result.endDateEpoch,
              };

              // Set the updated data back in the cache
              queryClient.setQueryData(queryKey, updatedData);
            }
          }
        });

        // Reset loading state for edit mode
        hasLoadedData.current = false;

        // Close the modal first
        onClose();

        // Call the onSaveComplete callback if provided (this will handle navigation)
        if (onSaveComplete) {
          onSaveComplete();
        }
      } else {
        // CREATE MODE - Invalidate to force a refetch
        await queryClient.invalidateQueries({
          queryKey: ['games'],
        });

        // Close the modal for create mode
        onClose();
      }

      return result;
    } catch (error) {
      console.error('Error saving game:', error);
      throw error;
    } finally {
      isSavingRef.current = false;
    }
  };

  // Handle modal close with state reset
  const handleClose = () => {
    // Reset the game state to initial state
    resetNewGameState();
    // Reset active step to first step
    setActiveStep(0);
    // Call the provided onClose function
    onClose();
  };

  return (
    <NewGameView
      steps={steps}
      activeStep={activeStep}
      onChangeActiveStep={onChangeActiveStep}
      newGameState={newGameState}
      onChangeNewGameState={onChangeNewGameState}
      onSaveGame={handleSaveGame}
      loading={loading}
      error={error}
      errorMessages={errorMessages}
      shouldDisableNextOrFinishButton={shouldDisableNextOrFinishButton}
      setShouldDisableNextOrFinishButton={setShouldDisableNextOrFinishButton}
      onClose={handleClose}
      isEditMode={isEditMode}
    />
  );
};

export default NewGameContainer;
